import { ChangeDetectionStrategy, Component, computed, input, inject } from '@angular/core';

import { ButtonModule } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { TooltipModule } from 'primeng/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faFile,
  faFileImage,
  faFileVideo,
  faFilePdf,
  faFileLines,
  faXmark,
  faRotate,
  faPlay,
  faExclamationTriangle,
  faCheck,
  faSpinner,
  faArrowUp,
  faCodeBranch,
  faTrash,
  faHourglass
} from '@fortawesome/pro-duotone-svg-icons';

import { UploadService } from '../../services/upload.service';
import { LocaleService } from '../../services/locale.service';
import { PlanLimitService } from '../../services/plan-limit.service';
import { UploadItem } from '../../models/upload.model';
import { formatFileSize } from '../../utils/file-details.utils';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-upload-item',
  standalone: true,
  imports: [
    ButtonModule,
    ProgressBarModule,
    TooltipModule,
    FontAwesomeModule,
    TranslateModule
  ],
  templateUrl: './upload-item.component.html',
  styleUrls: ['./upload-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UploadItemComponent {
  readonly upload = input.required<UploadItem>();
  readonly batchId = input.required<string>();

  private uploadService = inject(UploadService);
  private readonly localeService = inject(LocaleService);
  private readonly limits = inject(PlanLimitService);
  /** Composed live: a refused row's way out follows the plan and the reader's standing while the row stands, as the face's does. */
  protected readonly storageTail = computed(() => this.limits.storageTail());

  // Icons
  faFile = faFile;
  faFileImage = faFileImage;
  faFileVideo = faFileVideo;
  faFilePdf = faFilePdf;
  faFileLines = faFileLines;
  faXmark = faXmark;
  faRotate = faRotate;
  faPlay = faPlay;
  faExclamationTriangle = faExclamationTriangle;
  faCheck = faCheck;
  faSpinner = faSpinner;
  faArrowUp = faArrowUp;
  faCodeBranch = faCodeBranch;
  faTrash = faTrash;
  faHourglass = faHourglass;

  // Get file type icon — queued items show hourglass to indicate waiting
  getFileIcon(): any {
    if (this.upload().status === 'queued') {
      return this.faHourglass;
    }
    const type = this.getFileType();
    switch (type) {
      case 'image':
        return this.faFileImage;
      case 'video':
        return this.faFileVideo;
      case 'pdf':
        return this.faFilePdf;
      case 'document':
        return this.faFileLines;
      default:
        return this.faFile;
    }
  }

  // Get file type from content type
  getFileType(): string {
    if (!this.upload().type) return 'file';
    
    const contentTypePart = this.upload().type.split('/')[0];
    switch (contentTypePart) {
      case 'application':
        if (this.upload().type === 'application/pdf') return 'pdf';
        return 'document';
      case 'image':
      case 'video':
        return contentTypePart;
      default:
        return 'file';
    }
  }

  /** The row's size renders through the ONE size home, in the interface language. */
  formatSize(bytes: number): string {
    return formatFileSize(bytes, this.localeService.activeUILanguage());
  }

  // Get status icon
  getStatusIcon(): any {
    switch (this.upload().status) {
      case 'done':
        return this.faCheck;
      case 'failed':
      case 'alert':
        return this.faExclamationTriangle;
      case 'progress':
      case 'queued':
        return this.faSpinner;
      default:
        return null;
    }
  }

  // Get upload time
  getUploadTime(): number {
    const timestamps = this.upload().timestamps;
    if (!timestamps) return 0;

    const done = timestamps['done'];
    const progress = timestamps['progress'];

    if (done && progress) {
      return done.getTime() - progress.getTime();
    }
    return 0;
  }

  // Format duration
  formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }

  // Actions
  cancel() {
    if (this.canCancel()) {
      this.uploadService.cancelUpload(this.upload().id);
    }
  }

  canCancel(): boolean {
    return this.uploadService.canCancel(this.upload());
  }

  rename() {
    this.uploadService.renameUpload(this.upload().id);
  }

  uploadNewVersion() {
    this.uploadService.uploadNewVersion(this.upload().id);
  }

  remove() {
    this.uploadService.removeUpload(this.upload().id);
  }

  async resume(event: any) {
    const files = event.files || event.target?.files;
    if (files && files.length > 0) {
      await this.uploadService.resumeUpload(this.upload().id, files[0]);
    }
  }

  // Get progress color based on status
  getProgressColor(): string {
    switch (this.upload().status) {
      case 'done':
        return 'var(--green-500)';
      case 'failed':
      case 'alert':
        return 'var(--red-500)';
      default:
        return 'var(--primary-color)';
    }
  }

  // Check if upload is active
  isActive(): boolean {
    return this.upload().status === 'progress' || this.upload().status === 'queued';
  }

  // Check if upload has error
  hasError(): boolean {
    return this.upload().status === 'failed' || this.upload().status === 'alert';
  }

  // Get resume progress percentage
  getResumeProgress(): number {
    if (this.upload().size === 0) return 0;
    return Math.round((this.upload().bytesUploaded / this.upload().size) * 100);
  }

  // Get error message
  getErrorMessage(): string {
    if (this.upload().status === 'alert') {
      return 'upload.fileExists';
    }
    return this.upload().error || 'upload.uploadFailed';
  }

  // Handle file selection for resume (PrimeNG FileUpload)
  onFileSelect(event: any) {
    const file = event.files[0];
    if (file) {
      this.uploadService.resumeUpload(this.upload().id, file);
    }
  }

  // Handle file selection for resume (native file input)
  onResumeFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.uploadService.resumeUpload(this.upload().id, file);
      // Reset the input so the same file can be selected again if needed
      input.value = '';
    }
  }
}